import { forwardRef } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import MuiMenuItem, { MenuItemProps } from '@mui/material/MenuItem'
import { Casing, TEXT_ROLE_CASING } from '@common/helpers/casing'
import cn from 'classnames'
import { KEEP_MENU_OPEN_ATTRIBUTE } from '../Menu/Menu'
import { formatChildren } from '../Text'
import styles from './MenuItem.module.scss'

interface IProps extends MenuItemProps {
  /** Overrides the menu item role's case. */
  casing?: Casing
  /** Colors the item as a destructive action. */
  isDanger?: boolean
  /** A letter that runs the item while its menu is open (see Common/Menu), printed as a keycap. */
  shortcut?: string
  /**
   * Renders the item as a link to this route. A link wrapped around the item instead is not a
   * child of the list, so the arrow keys skip it.
   */
  to?: string
  /** Leaves the menu open after the item runs: a multi-select row, or one showing progress. */
  keepMenuOpen?: boolean
}

const MenuItem = forwardRef<HTMLLIElement, IProps>(
  (
    {
      casing = TEXT_ROLE_CASING.menuItem,
      isDanger,
      shortcut,
      to,
      keepMenuOpen,
      className,
      children,
      ...props
    },
    ref,
  ) => {
    const shortcutKey = shortcut?.toLowerCase()

    return (
      <MuiMenuItem
        {...props}
        {...(to && { component: RouterLink, to })}
        {...(keepMenuOpen && { [KEEP_MENU_OPEN_ATTRIBUTE]: true })}
        {...(shortcutKey && {
          'data-menu-shortcut': shortcutKey,
          'aria-keyshortcuts': shortcutKey.toUpperCase(),
        })}
        ref={ref}
        className={cn(className, { [styles.withShortcut]: shortcutKey, [styles.danger]: isDanger })}
        classes={{ root: styles.root }}
      >
        {formatChildren(casing, children)}
        {shortcutKey && (
          <kbd className={styles.shortcut} aria-hidden="true">
            {shortcutKey.toUpperCase()}
          </kbd>
        )}
      </MuiMenuItem>
    )
  },
)

export default MenuItem
